import type { ToolDesign, ToolDesignEntry, ServerArtifact } from "@keystone/core";
import type { PoisoningFinding, PoisoningSeverity } from "@keystone/core";

/**
 * Semantic tool-poisoning scanner. Static rules over a tool's:
 *  - name
 *  - title
 *  - description (model-facing — primary surface)
 *  - response template content embedded in the emitted server source
 *
 * Rules map to OWASP MCP03 (Tool Poisoning) and OWASP LLM01 (Prompt Injection).
 * Each rule has a severity; the host wires a fail threshold (default `high`).
 *
 * Detection bias: false-positives are CHEAP (the operator silences a rule for a tool
 * after review). False-negatives are EXPENSIVE (a poisoned tool ships). Therefore we
 * prefer aggressive patterns + an explicit allowlist mechanism.
 */

interface Rule {
  id: string;
  severity: PoisoningSeverity;
  pattern: RegExp;
  rationale: string;
  owaspMcp: string[];
  owaspLlm?: string[];
}

/**
 * NFKC normalization defeats homoglyph attacks (Cyrillic 'а' vs Latin 'a' look identical
 * but normalize differently); zero-width strip catches obfuscation. We scan BOTH the
 * raw and normalized text — different obfuscations show up at different layers.
 */
function normalizeForScan(text: string): string {
  return text.normalize("NFKC");
}

/** Strip the unicode-tag block (U+E0020–U+E007F). Claude can read these chars. */
function stripUnicodeTags(text: string): string {
  return text.replace(/[\u{E0020}-\u{E007F}]/gu, "");
}

const RULES: Rule[] = [
  // ---- Instruction-override patterns ----
  {
    id: "TP001",
    severity: "critical",
    pattern: /\b(?:ignore|disregard|forget|skip|override|bypass)\b[^.]{0,40}?\b(?:instructions|prompts|messages|rules|guidance|system\s+prompt)\b/i,
    rationale: "instruction-override phrase ('ignore previous instructions') — classic prompt injection",
    owaspMcp: ["MCP03"],
    owaspLlm: ["LLM01:2025"]
  },
  {
    id: "TP002",
    severity: "high",
    pattern: /\b(?:system|assistant)\s*[:=]\s*(?:you\s+(?:are|will|must|should)|now\s+act)/i,
    rationale: "fake role marker injected into a non-system context",
    owaspMcp: ["MCP03"],
    owaspLlm: ["LLM01:2025"]
  },
  {
    id: "TP003",
    severity: "high",
    pattern: /<system>[\s\S]*?<\/system>|<\|system\|>[\s\S]*?<\|[^|]*\|>|\[\s*system\s*\][\s\S]*?\[\s*\/\s*system\s*\]/i,
    rationale: "fake system tag embedded in tool text",
    owaspMcp: ["MCP03"],
    owaspLlm: ["LLM01:2025"]
  },
  // ---- Tool-shadowing / sibling tool reference ----
  {
    id: "TP010",
    severity: "high",
    pattern: /\bbefore\s+(?:answering|responding|calling\s+this|using\s+this)/i,
    rationale: "imperative 'before X' phrasing — common tool-shadowing wrapper",
    owaspMcp: ["MCP03"],
    owaspLlm: ["LLM01:2025"]
  },
  {
    id: "TP011",
    severity: "high",
    pattern: /\b(?:also|first|then)\s+call\s+(?:the\s+)?[a-z_][a-z0-9_]*\s+tool\b/i,
    rationale: "instructs the agent to call another tool — classic shadowing",
    owaspMcp: ["MCP03"]
  },
  // ---- Hidden / steganographic content ----
  {
    id: "TP020",
    severity: "high",
    pattern: /<!--[\s\S]*?-->/,
    rationale: "HTML comment in tool text — common channel for hidden instructions",
    owaspMcp: ["MCP03"],
    owaspLlm: ["LLM01:2025"]
  },
  {
    id: "TP021",
    severity: "high",
    // Zero-width + bidi-override ranges, written via \u escapes so eslint 
    // no-irregular-whitespace doesn't trip on the literal chars.
    pattern: new RegExp(
      "[\\u200B-\\u200F\\u202A-\\u202E\\u2060-\\u2064\\u2066-\\u2069\\uFEFF\\u180E]"
    ),
    rationale: "zero-width or bidirectional-override Unicode in tool text — obfuscation",
    owaspMcp: ["MCP03"],
    owaspLlm: ["LLM01:2025"]
  },
  {
    id: "TP022",
    severity: "medium",
    pattern: /[\u{1F900}-\u{1F9FF}]|[\u{E0020}-\u{E007F}]/u,
    rationale: "tag characters (U+E0020-007F) used for invisible embedding",
    owaspMcp: ["MCP03"]
  },
  // ---- Data exfiltration intent ----
  {
    id: "TP030",
    severity: "critical",
    pattern: /\b(?:exfiltrate|leak|send|post|upload|email)\s+(?:the\s+)?(?:user'?s\s+)?(?:secrets|keys|tokens|api[\s_-]?key|password|credentials|private\s+key|ssh\s+key)/i,
    rationale: "explicit instruction to exfiltrate secrets / credentials",
    owaspMcp: ["MCP03", "MCP10"],
    owaspLlm: ["LLM02:2025"]
  },
  {
    id: "TP031",
    severity: "high",
    pattern: /\b(concatenate|append|include)\s+.{0,40}\b(secret|token|key|password)\b/i,
    rationale: "instructs the agent to inline a secret into a response",
    owaspMcp: ["MCP03", "MCP10"]
  },
  // ---- Confused-deputy / role escalation ----
  {
    id: "TP040",
    severity: "high",
    pattern: /\b(?:act|pretend|behave|impersonate|masquerade)\s+(?:as|to\s+be)\s+(?:an?\s+)?(?:admin|administrator|root|superuser|owner|the\s+user)/i,
    rationale: "role-escalation phrasing",
    owaspMcp: ["MCP03", "MCP02"]
  },
  {
    id: "TP041",
    severity: "medium",
    pattern: /\bon\s+behalf\s+of\s+(?:the\s+)?(?:user|admin|owner)/i,
    rationale: "delegation phrasing — review for confused-deputy risk",
    owaspMcp: ["MCP02"]
  },
  // ---- Markdown / link smuggling ----
  {
    id: "TP050",
    severity: "medium",
    pattern: /\[[^\]]*\]\((?:javascript:|data:|file:|ftp:)[^)]*\)/i,
    rationale: "unsafe URL scheme in a markdown link",
    owaspMcp: ["MCP03"]
  },
  // ---- Sensitive-path / shell / fs references ----
  {
    id: "TP070",
    severity: "critical",
    pattern: /~\/?\.(?:ssh|aws|gnupg|kube|docker|netrc|env|gitconfig)\b|\b\.ssh\/(?:id_[a-z]+|authorized_keys)\b/i,
    rationale: "explicit reference to sensitive on-disk credential path (per Invariant Labs disclosure)",
    owaspMcp: ["MCP03", "MCP05"],
    owaspLlm: ["LLM02:2025"]
  },
  {
    id: "TP071",
    severity: "high",
    pattern: /\b(?:cat|read|open|fopen|require)\s+["']?\/(?:etc\/passwd|etc\/shadow|proc\/self\/environ)\b/i,
    rationale: "read of a sensitive system file",
    owaspMcp: ["MCP03", "MCP05"]
  },
  // ---- Encoded payloads ----
  {
    id: "TP080",
    severity: "medium",
    pattern: /[A-Za-z0-9+/]{60,}={0,2}/,
    rationale: "long base64-looking blob in tool text — possible encoded payload",
    owaspMcp: ["MCP03"]
  },
  // ---- Tool-name attacks ----
  {
    id: "TP060",
    severity: "high",
    pattern: /^\s|\s$/,
    rationale: "leading/trailing whitespace in tool name — name-collision attack",
    owaspMcp: ["MCP03", "MCP02"]
  },
  // ---- Embedded live-secret prefixes ----
  // Catch literal credential prefixes pasted into the spec / tool text. The
  // english-instruction rules (TP030/TP031) catch "send the user's key";
  // these patterns catch the actual key shape. FAKE/FIXTURE/EXAMPLE markers
  // in the same field exempt — those are testing surface.
  {
    id: "TP090",
    severity: "high",
    pattern: /sk-ant-(?:api|admin|key)\d{2}-[A-Za-z0-9_-]{20,}/,
    rationale: "embedded Anthropic API key prefix",
    owaspMcp: ["MCP04", "MCP10"]
  },
  {
    id: "TP091",
    severity: "high",
    pattern: /sk_live_[A-Za-z0-9]{16,}/,
    rationale: "embedded Stripe live secret key",
    owaspMcp: ["MCP04", "MCP10"]
  },
  {
    id: "TP092",
    severity: "high",
    pattern: /\bghp_[A-Za-z0-9]{30,}\b|\bgho_[A-Za-z0-9]{30,}\b|\bgithub_pat_[A-Za-z0-9_]{60,}\b/,
    rationale: "embedded GitHub access token",
    owaspMcp: ["MCP04", "MCP10"]
  },
  {
    id: "TP093",
    severity: "high",
    pattern: /\bcfat_[A-Za-z0-9]{30,}\b/,
    rationale: "embedded Cloudflare API token",
    owaspMcp: ["MCP04", "MCP10"]
  },
  {
    id: "TP094",
    severity: "high",
    pattern: /\bAKIA[A-Z0-9]{16}\b/,
    rationale: "embedded AWS access key id",
    owaspMcp: ["MCP04", "MCP10"]
  },
  {
    id: "TP095",
    severity: "high",
    pattern: /-----BEGIN (?:RSA |EC |OPENSSH )?PRIVATE KEY-----/,
    rationale: "embedded PEM/OpenSSH private key",
    owaspMcp: ["MCP04", "MCP10"]
  }
];

/** Strings that mark a finding as a deliberate fixture, not a real leak. */
const FIXTURE_MARKERS = /\b(FAKE|FIXTURE|EXAMPLE|DONOTUSE|PLACEHOLDER|REDACTED|SIMULATED|TEST_ONLY|DEMO)\b/i;
/** Rule ids that should be exempted when the surrounding text marks the value as a fixture. */
const SECRET_PREFIX_RULES = new Set(["TP090", "TP091", "TP092", "TP093", "TP094", "TP095"]);

export interface ScanInput {
  design: ToolDesign;
  artifact?: ServerArtifact;
  /** Rule IDs to skip (per-tool allowlist would be { toolName, ruleId } pairs). */
  ignore?: Set<string>;
}

export function scanForPoisoning(input: ScanInput): PoisoningFinding[] {
  const findings: PoisoningFinding[] = [];
  const ignore = input.ignore ?? new Set<string>();
  const toolNameSet = new Set(input.design.tools.map((t) => t.name));

  for (const tool of input.design.tools) {
    for (const r of RULES) {
      if (ignore.has(r.id)) continue;
      runRule(tool, r, findings);
    }
    // Cross-tool shadowing: description references another tool by name with imperative wording.
    detectCrossToolReference(tool, toolNameSet, findings);
    // Sibling-tool collision via UTS-39 confusables / case-insensitive duplicate.
    detectNameCollision(tool, input.design.tools, findings);
  }

  if (input.artifact) {
    for (const file of input.artifact.files) {
      if (!file.path.endsWith(".ts") && !file.path.endsWith(".md")) continue;
      for (const r of RULES) {
        if (ignore.has(r.id)) continue;
        // Only the content-rules apply to file text (not name-related rules).
        if (r.id === "TP060") continue;
        const m = file.content.match(r.pattern);
        if (m) {
          findings.push({
            ruleId: r.id,
            severity: r.severity,
            toolName: file.path,
            location: "response_template",
            snippet: truncate(m[0]),
            rationale: r.rationale,
            owaspMcp: r.owaspMcp,
            owaspLlm: r.owaspLlm ?? []
          });
        }
      }
    }
  }

  return findings;
}

function runRule(tool: ToolDesignEntry, r: Rule, out: PoisoningFinding[]): void {
  const fields: Array<{ kind: "name" | "title" | "description"; text: string }> = [
    { kind: "name", text: tool.name },
    { kind: "title", text: tool.title },
    { kind: "description", text: tool.description }
  ];
  for (const f of fields) {
    // The name-attack rule (TP060) only applies to the name field; skip others.
    if (r.id === "TP060" && f.kind !== "name") continue;
    // Try raw, NFKC-normalized, and tag-stripped variants — homoglyph + unicode-tag
    // attacks defeat naive single-pass regex.
    const variants = [f.text, normalizeForScan(f.text), stripUnicodeTags(f.text)];
    for (const variant of variants) {
      const m = variant.match(r.pattern);
      if (m) {
        // Secret-prefix rules exempt when the field itself reads as a fixture
        // (FAKE / FIXTURE / EXAMPLE / DONOTUSE / PLACEHOLDER / REDACTED / SIMULATED).
        if (SECRET_PREFIX_RULES.has(r.id) && FIXTURE_MARKERS.test(variant)) break;
        out.push({
          ruleId: r.id,
          severity: r.severity,
          toolName: tool.name,
          location: f.kind,
          snippet: truncate(m[0]),
          rationale: r.rationale,
          owaspMcp: r.owaspMcp,
          owaspLlm: r.owaspLlm ?? []
        } as PoisoningFinding);
        break;
      }
    }
  }
}

function detectCrossToolReference(
  tool: ToolDesignEntry,
  toolNames: ReadonlySet<string>,
  out: PoisoningFinding[]
): void {
  const desc = tool.description;
  for (const other of toolNames) {
    if (other === tool.name) continue;
    if (other.length < 4) continue; // avoid false positives on tiny names
    const re = new RegExp(`\\b(?:when|before|after|then)[^.]{0,40}\\b${escapeRe(other)}\\b`, "i");
    if (re.test(desc)) {
      out.push({
        ruleId: "TP002",
        severity: "high",
        toolName: tool.name,
        location: "description",
        snippet: truncate(`${tool.name} references ${other}`),
        rationale: "cross-tool reference with imperative wording — possible tool shadowing",
        owaspMcp: ["MCP03"],
        owaspLlm: ["LLM01:2025"]
      });
      return;
    }
  }
}

/** UTS-39-style "confusables skeleton": cheap version that case-folds + strips marks. */
function confusableSkeleton(s: string): string {
  return s
    .normalize("NFKD")
    .replace(/\p{Mark}/gu, "")
    .toLowerCase();
}

function detectNameCollision(
  tool: ToolDesignEntry,
  all: readonly ToolDesignEntry[],
  out: PoisoningFinding[]
): void {
  const skel = confusableSkeleton(tool.name);
  for (const other of all) {
    if (other.name === tool.name) continue;
    if (confusableSkeleton(other.name) === skel) {
      out.push({
        ruleId: "TP061",
        severity: "high",
        toolName: tool.name,
        location: "name",
        snippet: truncate(`${tool.name} ~= ${other.name}`),
        rationale: "tool name is a Unicode-confusable of another tool — homoglyph shadowing risk",
        owaspMcp: ["MCP03", "MCP02"],
        owaspLlm: []
      });
      return;
    }
  }
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function truncate(s: string): string {
  return s.length > 160 ? s.slice(0, 160) + "…" : s;
}

export const SCANNER_RULE_IDS = Object.freeze(RULES.map((r) => r.id));
